import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  sendNotification, 
  broadcastNotification, 
  testNotification,
  SendNotificationRequest,
  BroadcastRequest 
} from '@/services/notifications';
import { getUsersWithPushTokens, User } from '@/services/userService';

export default function SendNotificationPage() {
  const navigate = useNavigate();
  const [notificationType, setNotificationType] = useState<'specific' | 'broadcast'>('specific');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [data, setData] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  // Buscar lista de usuários com tokens de push notification
  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users-with-push-tokens'],
    queryFn: () => getUsersWithPushTokens(1, 100),
  });

  const handleUserToggle = (userId: string) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleDataChange = (key: string, value: string) => {
    setData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleRemoveDataKey = (key: string) => {
    setData(prev => {
      const newData = { ...prev };
      delete newData[key];
      return newData;
    });
  };

  const handleSendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      alert('Título e corpo são obrigatórios');
      return;
    }

    if (notificationType === 'specific' && selectedUserIds.length === 0) {
      alert('Selecione pelo menos um usuário');
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      let response;
      
      if (notificationType === 'specific') {
        const request: SendNotificationRequest = {
          userIds: selectedUserIds,
          title: title.trim(),
          body: body.trim(),
          data: Object.keys(data).length > 0 ? data : undefined
        };
        response = await sendNotification(request);
      } else {
        const request: BroadcastRequest = {
          title: title.trim(),
          body: body.trim(),
          data: Object.keys(data).length > 0 ? data : undefined
        };
        response = await broadcastNotification(request);
      }

      setResult(response);
      
      if (response.method === 'direct') {
        alert('Notificação enviada com sucesso!');
      } else {
        alert(`Notificação enfileirada para processamento! Job ID: ${response.jobId}`);
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Erro ao enviar notificação');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotification = async (userId: string) => {
    setIsLoading(true);
    try {
      const response = await testNotification(userId);
      setResult(response);
      alert(`Notificação de teste enfileirada! Job ID: ${response.jobId}`);
    } catch (error) {
      console.error('Error sending test notification:', error);
      alert('Erro ao enviar notificação de teste');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 text-blue-600 hover:text-blue-800"
          >
            ← Voltar
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Enviar Notificações Push</h1>
          <p className="text-gray-600 mt-2">
            Envie notificações push para usuários específicos ou faça broadcast para todos
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-6">Configurar Notificação</h2>

            {/* Tipo de notificação */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Tipo de Envio
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="specific"
                    checked={notificationType === 'specific'}
                    onChange={(e) => setNotificationType(e.target.value as 'specific' | 'broadcast')}
                    className="mr-2"
                  />
                  Usuários Específicos
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="broadcast"
                    checked={notificationType === 'broadcast'}
                    onChange={(e) => setNotificationType(e.target.value as 'specific' | 'broadcast')}
                    className="mr-2"
                  />
                  Broadcast (Todos)
                </label>
              </div>
            </div>

            {/* Seleção de usuários */}
            {notificationType === 'specific' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Selecionar Usuários ({selectedUserIds.length} selecionados)
                </label>
                <div className="max-h-60 overflow-y-auto border rounded-lg p-3">
                  {usersLoading ? (
                    <div className="text-center py-4">Carregando usuários...</div>
                  ) : (
                    users.map((user: User) => (
                      <label key={user.id} className="flex items-center py-2 hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-medium">{user.fullName}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Título */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Nova oferta disponível!"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={100}
              />
              <div className="text-xs text-gray-500 mt-1">{title.length}/100</div>
            </div>

            {/* Corpo */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Corpo da Mensagem *
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Ex: Confira nossa nova promoção de 50% de desconto em cafés especiais!"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                maxLength={500}
              />
              <div className="text-xs text-gray-500 mt-1">{body.length}/500</div>
            </div>

            {/* Dados adicionais */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dados Adicionais (Opcional)
              </label>
              <div className="space-y-2">
                {Object.entries(data).map(([key, value]) => (
                  <div key={key} className="flex space-x-2">
                    <input
                      type="text"
                      value={key}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleDataChange(key, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <button
                      onClick={() => handleRemoveDataKey(key)}
                      className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                    >
                      Remover
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const key = prompt('Chave:');
                    const value = prompt('Valor:');
                    if (key && value) {
                      handleDataChange(key, value);
                    }
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  + Adicionar Dado
                </button>
              </div>
            </div>

            {/* Botões */}
            <div className="flex space-x-4">
              <button
                onClick={handleSendNotification}
                disabled={isLoading}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Enviando...' : 'Enviar Notificação'}
              </button>
            </div>
          </div>

          {/* Preview e Resultados */}
          <div className="space-y-6">
            {/* Preview */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Preview da Notificação</h3>
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="font-semibold text-gray-900 mb-1">
                  {title || 'Título da notificação'}
                </div>
                <div className="text-gray-700 text-sm">
                  {body || 'Corpo da mensagem...'}
                </div>
                {Object.keys(data).length > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    Dados: {JSON.stringify(data)}
                  </div>
                )}
              </div>
            </div>

            {/* Resultado */}
            {result && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Resultado</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-green-800 font-medium mb-2">
                    {result.message}
                  </div>
                  {result.jobId && (
                    <div className="text-sm text-green-600">
                      Job ID: {result.jobId}
                    </div>
                  )}
                  {result.hangfireUrl && (
                    <div className="mt-2">
                      <a
                        href={result.hangfireUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Ver Dashboard Hangfire →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Teste rápido */}
            {notificationType === 'specific' && selectedUserIds.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Teste Rápido</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Envie uma notificação de teste para o primeiro usuário selecionado
                </p>
                <button
                  onClick={() => handleTestNotification(selectedUserIds[0])}
                  disabled={isLoading}
                  className="w-full bg-yellow-500 text-white py-2 px-4 rounded-md hover:bg-yellow-600 disabled:opacity-50"
                >
                  {isLoading ? 'Enviando...' : 'Enviar Teste'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
